import { AztecAddress } from "@aztec/aztec.js";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { getL2PrivateBalance, getL2PublicBalance, getL2PublicBalancesBatch } from "../actions/aztecBalance";
import { l2AccountAddressQuery } from "./aztec";
import { useBridgedTokens } from "./tokenReg";

export type TokenBalance = {
  tokenAddress: string;
  tokenName: string;
  tokenSymbol: string;
  tokenDecimals: number;
  publicBalance: bigint;
  privateBalance?: bigint; // Optional - fetched on-demand
};

/**
 * Fetches a SINGLE public balance on-demand.
 * This mirrors usePrivateBalance() for consistency.
 */
export function usePublicBalance(tokenAddress: string, enabled = true) {
  const { data: accountAddress } = useQuery(l2AccountAddressQuery);

  const publicBalanceQuery = useQuery({
    queryKey: ["public-balance", tokenAddress, accountAddress?.toString()],
    queryFn: async () => {
      if (!accountAddress) return 0n;

      const l2TokenAddress = AztecAddress.fromString(tokenAddress);
      return await getL2PublicBalance(l2TokenAddress, accountAddress);
    },
    enabled: enabled && !!accountAddress && !!tokenAddress && tokenAddress !== AztecAddress.ZERO.toString(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  return {
    publicBalance: publicBalanceQuery.data,
    isLoading: publicBalanceQuery.isLoading,
    isPending: publicBalanceQuery.isPending,
    error: publicBalanceQuery.error,
    refetch: publicBalanceQuery.refetch,
  };
}

/**
 * Fetches all PUBLIC balances efficiently in batches and stores them individually.
 * This maintains batch fetching performance while enabling individual cache entries.
 */
export function usePublicBalances() {
  const { data: accountAddress } = useQuery(l2AccountAddressQuery);
  const { data: bridgedTokens, isPending: tokensPending } = useBridgedTokens();
  const queryClient = useQueryClient();

  // Filter tokens that have L2 addresses
  const tokensToFetch = useMemo(() => {
    return bridgedTokens?.filter((t) => t.l2Address) || [];
  }, [bridgedTokens]);

  // Batch fetch all public balances and store them individually in cache
  useEffect(() => {
    if (!accountAddress || !tokensToFetch || tokensToFetch.length === 0) {
      return;
    }

    const fetchAndCacheBalances = async () => {
      const BATCH_SIZE = 4; // PXE errors if we do >4 in a single batch

      for (let i = 0; i < tokensToFetch.length; i += BATCH_SIZE) {
        const batch = tokensToFetch.slice(i, i + BATCH_SIZE);

        try {
          const batchBalances = await getL2PublicBalancesBatch(
            batch.map((t) => ({
              l1Address: t.tokenAddress,
              l2Address: t.l2Address!,
            })),
            accountAddress,
          );

          // Store each balance individually in cache
          for (const [l2Address, balance] of batchBalances) {
            queryClient.setQueryData(
              ["public-balance", l2Address, accountAddress.toString()],
              balance,
            );
          }
        } catch (error) {
          console.error(`Failed to fetch public balance batch ${i / BATCH_SIZE + 1}:`, error);
          // Set zero balances for failed batch
          for (const token of batch) {
            queryClient.setQueryData(
              ["public-balance", token.l2Address!, accountAddress.toString()],
              0n,
            );
          }
        }
      }
    };

    fetchAndCacheBalances();
  }, [accountAddress, tokensToFetch, queryClient]);

  // Use useQueries to watch all individual balance queries
  const balanceQueries = useQueries({
    queries: tokensToFetch.map((token) => ({
      queryKey: ["public-balance", token.l2Address!, accountAddress?.toString()],
      queryFn: async () => {
        if (!accountAddress) return 0n;
        const l2TokenAddress = AztecAddress.fromString(token.l2Address!);
        return await getL2PublicBalance(l2TokenAddress, accountAddress);
      },
      enabled: !!accountAddress && !!token.l2Address,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 60 * 60 * 1000, // 1 hour
    })),
  });

  // Construct Map from individual cache entries
  const publicBalances = useMemo(() => {
    const map = new Map<string, bigint>();
    for (let i = 0; i < tokensToFetch.length; i++) {
      const token = tokensToFetch[i];
      const balance = balanceQueries[i]?.data ?? 0n;
      if (token?.l2Address) {
        map.set(token.l2Address, balance);
      }
    }
    return map;
  }, [tokensToFetch, balanceQueries]);

  const isLoading = tokensPending || balanceQueries.some((q) => q.isLoading);
  const isPending = tokensPending || balanceQueries.some((q) => q.isPending);

  return {
    publicBalances,
    isLoading,
    isPending,
    error: balanceQueries.find((q) => q.error)?.error,
    refetch: () => {
      balanceQueries.forEach((q) => q.refetch());
    },
  };
}

/**
 * Fetches a SINGLE private balance on-demand.
 * Private balances are slow (individual RPC calls) so we only fetch them when needed.
 */
export function usePrivateBalance(tokenAddress: string, enabled = true) {
  const { data: accountAddress } = useQuery(l2AccountAddressQuery);

  const privateBalanceQuery = useQuery({
    queryKey: ["private-balance", tokenAddress, accountAddress?.toString()],
    queryFn: async () => {
      if (!accountAddress) return 0n;

      const l2TokenAddress = AztecAddress.fromString(tokenAddress);
      return await getL2PrivateBalance(l2TokenAddress, accountAddress);
    },
    enabled: enabled && !!accountAddress && !!tokenAddress && tokenAddress !== AztecAddress.ZERO.toString(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  return {
    privateBalance: privateBalanceQuery.data,
    isLoading: privateBalanceQuery.isLoading,
    isPending: privateBalanceQuery.isPending,
    error: privateBalanceQuery.error,
    refetch: privateBalanceQuery.refetch,
  };
}

/**
 * Main hook that combines public balances with token metadata.
 * Private balances are NOT included here - use usePrivateBalance() to fetch them on-demand.
 */
export function useTokenBalances() {
  const { data: bridgedTokens, isPending: tokensPending } = useBridgedTokens();
  const { publicBalances, isLoading, error, refetch } = usePublicBalances();

  // Convert to full TokenBalance objects with token metadata
  const balancesMap = useMemo(() => {
    if (!bridgedTokens) return new Map<string, TokenBalance>();

    const map = new Map<string, TokenBalance>();
    for (const token of bridgedTokens) {
      if (!token.l2Address) continue;

      const publicBalance = publicBalances.get(token.l2Address) ?? 0n;
      map.set(token.l2Address, {
        tokenAddress: token.l2Address,
        tokenName: token.tokenName,
        tokenSymbol: token.tokenSymbol,
        tokenDecimals: token.tokenDecimals,
        publicBalance,
        // privateBalance is undefined - fetch on-demand with usePrivateBalance()
      });
    }
    return map;
  }, [bridgedTokens, publicBalances]);

  // Convert map to array for easier consumption
  const balancesArray = useMemo(() => {
    return Array.from(balancesMap.values());
  }, [balancesMap]);

  // Get a specific token balance
  const getBalance = (tokenAddress: string): TokenBalance | undefined => {
    return balancesMap.get(tokenAddress);
  };

  return {
    balances: balancesMap,
    balancesArray,
    getBalance,
    isLoading: tokensPending || isLoading,
    isPending: tokensPending || isLoading,
    error,
    refetch,
  };
}

/**
 * Hook to get a single token's public balance from the cached data.
 * For private balance, use usePrivateBalance() separately.
 */
export function useTokenBalance(tokenAddress: string) {
  const { balances, isLoading, error } = useTokenBalances();

  const balance = balances.get(tokenAddress);

  return {
    data: balance,
    publicBalance: balance?.publicBalance,
    isLoading,
    error,
  };
}

/**
 * Hook to manually refetch balances for a specific token.
 */
export function useRefetchTokenBalances() {
  const { data: accountAddress } = useQuery(l2AccountAddressQuery);
  const queryClient = useQueryClient();

  const refetchPublicBalance = (tokenAddress: string) => {
    queryClient.invalidateQueries({
      queryKey: ["public-balance", tokenAddress, accountAddress?.toString()],
    });
  };

  const refetchPrivateBalance = (tokenAddress: string) => {
    queryClient.invalidateQueries({
      queryKey: ["private-balance", tokenAddress, accountAddress?.toString()],
    });
  };

  const refetchBothBalances = (tokenAddress: string) => {
    refetchPublicBalance(tokenAddress);
    refetchPrivateBalance(tokenAddress);
  };

  return {
    refetchPublicBalance,
    refetchPrivateBalance,
    refetchBothBalances,
  };
}
